// angular
import {
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
// directives
import { OnlyNumberDirective } from '@app/shared/directives/only-number.directive';
import { IPhoneNumber } from '@app/core/shared/interfaces/phone-number.interface';
import { IDropdown } from '@app/shared/types/dropdown.interface';
import { CountryCodes } from '@shared/constants/country-codes.const';

@Component({
  selector: 'pwj-phone-number',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    OnlyNumberDirective,
    TranslateModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneNumberComponent),
      multi: true,
    },
  ],
  templateUrl: './phone-number.component.html',
  styleUrl: './phone-number.component.scss',
})
export class PhoneNumberComponent implements OnInit, ControlValueAccessor {
  label = input<string>('');
  subLabel = input<string>('');
  placeholder = input<string>('');
  isDisabled = input<boolean>(false);
  icon = input<string>('');
  inputClasses = input<string>('');
  messages = input<{ [errorCode: string]: string }>({
    required: 'requires.phoneNumber',
    invalidMobile: 'requires.invalidMobile',
  });

  focus = output<boolean>();
  blur = output<boolean>();

  public value: string | null = null;
  public disabled = false;
  public phoneNumberForm: FormGroup;
  public countryCodes: IDropdown[];
  public showPrefixMenu = false;
  public searchTerm = '';
  public selectedCountryCode = 'GB';

  private readonly _fb = inject(FormBuilder);
  private readonly _elementRef = inject(ElementRef);

  constructor() {
    this.countryCodes = CountryCodes.map(code => ({
      value: code.dial_code,
      label: `${code.code}+${code.name}${code.dial_code}`,
    }));

    this.phoneNumberForm = this._fb.group({
      prefix: ['+44'],
      mobile_number: ['', [Validators.required, this.validateMobileNumber()]],
    });
  }

  validateMobileNumber(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value || !/^\d+$/.test(value)) {
        return { invalidMobile: true };
      }

      const length = value.length;

      if (length > 7 && length < 12) {
        return null;
      }

      return { invalidMobile: true };
    };
  }

  ngOnInit() {
    this.isDisabled() && this.setDisabledState(this.isDisabled());

    this.phoneNumberForm.valueChanges.subscribe((val: IPhoneNumber) => {
      if (this.phoneNumberForm.valid) {
        this.propagateChange(val);
      } else {
        this.propagateChange(null);
      }
    });

    this.phoneNumberForm.statusChanges.subscribe(() => {
      this.onTouched();
    });
  }

  get f() {
    return this.phoneNumberForm.controls;
  }

  get filteredCountryCodes(): IDropdown[] {
    if (!this.searchTerm.trim()) {
      return this.countryCodes;
    }

    const term = this.searchTerm.trim().toLowerCase();
    return this.countryCodes.filter(
      option =>
        option.label.toLowerCase().includes(term) ||
        option.value.toLowerCase().includes(term)
    );
  }

  get hasErrors(): boolean {
    const control = this.phoneNumberForm.get('mobile_number');
    return !!(
      control?.errors &&
      (control.touched || control.dirty) &&
      control.invalid
    );
  }

  get mobileErrors(): ValidationErrors | null {
    return this.phoneNumberForm.get('mobile_number')?.errors ?? null;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this._elementRef.nativeElement.contains(event.target)) {
      this.closePrefixMenu();
    }
  }

  propagateChange = (_: IPhoneNumber | null) => {};

  onTouched: () => void = () => {};

  writeValue(value: IPhoneNumber | string | null) {
    if (!value) {
      this.phoneNumberForm.reset({
        prefix: '+44',
        mobile_number: '',
      });
      this.selectedCountryCode = 'GB';
      return;
    }

    if (typeof value === 'string') {
      this.phoneNumberForm.get('mobile_number')?.setValue(value);
      return;
    }

    this.f['prefix'].setValue(value.prefix ?? '+44');
    this.f['mobile_number'].setValue(value.mobile_number ?? '');
    this.selectedCountryCode =
      CountryCodes.find(code => code.dial_code === value.prefix)?.code ?? 'GB';
  }

  registerOnChange(fn: () => void): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  validate(): ValidationErrors | null {
    return this.phoneNumberForm.valid ? null : { invalidPhoneNumber: true };
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.f['prefix'].disable();
      this.f['mobile_number'].disable();
    } else {
      this.f['prefix'].enable();
      this.f['mobile_number'].enable();
    }
    this.disabled = isDisabled;
    this.closePrefixMenu();
  }

  togglePrefixMenu(event: Event): void {
    event.stopPropagation();
    if (this.disabled) {
      return;
    }
    this.showPrefixMenu = !this.showPrefixMenu;
    if (!this.showPrefixMenu) {
      this.searchTerm = '';
    }
  }

  selectCountry(option: IDropdown, event: Event): void {
    event.stopPropagation();
    this.f['prefix'].setValue(option.value);
    this.selectedCountryCode = option.label.split('+')[0];
    this.f['prefix'].markAsDirty();
    this.closePrefixMenu();
  }

  closePrefixMenu(): void {
    this.showPrefixMenu = false;
    this.searchTerm = '';
  }

  getCountryCode(option: IDropdown): string {
    return option.label.split('+')[0];
  }

  getCountryName(option: IDropdown): string {
    return option.label.split('+')[1];
  }

  getDialCode(option: IDropdown): string {
    return option.label.split('+')[2];
  }

  isSelectedCountry(option: IDropdown): boolean {
    return this.selectedCountryCode === this.getCountryCode(option);
  }

  onMobileFocus(): void {
    this.focus.emit(true);
  }

  onMobileBlur(): void {
    this.phoneNumberForm.get('mobile_number')?.markAsTouched();
    this.onTouched();
    this.blur.emit(true);
  }
}
